import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAboutYou,
  nextStep,
  selectAboutYou,
} from "../../../store/slices/registrationSlice";
import {
  User,
  GenderFemale,
  GenderMale,
  GenderNonbinary,
  ArrowLeft,
} from "@phosphor-icons/react";
import Button from "../../ui/CustomClinikitButton";
import { scrollToFirstError } from "../../../utils/formScroll";
import "./Steps.css";

const genderOptions = [
  { value: "female", label: "נקבה", icon: GenderFemale },
  { value: "male", label: "זכר", icon: GenderMale },
  { value: "other", label: "אחר", icon: GenderNonbinary },
];

function AboutYouStep() {
  const dispatch = useDispatch();
  const savedInfo = useSelector(selectAboutYou);

  const [formData, setFormData] = useState({
    fullName: savedInfo.fullName || "",
    email: savedInfo.email || "",
    gender: savedInfo.gender || "",
    licenseNumber: savedInfo.licenseNumber || "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const formRef = useRef(null);

  const validateField = (name, value) => {
    switch (name) {
      case "fullName":
        if (!value.trim()) return "שדה חובה";
        if (value.trim().length < 2) return "השם קצר מדי";
        return "";
      case "email":
        if (!value.trim()) return "שדה חובה";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "כתובת אימייל לא תקינה";
        return "";
      case "gender":
        if (!value) return "יש לבחור לשון פנייה";
        return "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.fullName = validateField("fullName", formData.fullName);
    newErrors.email = validateField("email", formData.email);
    newErrors.gender = validateField("gender", formData.gender);

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );

    setErrors(filteredErrors);
    // Mark everything touched so submit surfaces errors on untouched fields too
    setTouched({ fullName: true, email: true, gender: true });
    return Object.keys(filteredErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleGenderSelect = (value) => {
    setFormData((prev) => ({ ...prev, gender: value }));
    setTouched((prev) => ({ ...prev, gender: true }));
    setErrors((prev) => ({ ...prev, gender: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      dispatch(setAboutYou(formData));
      dispatch(nextStep());
    } else {
      scrollToFirstError(formRef.current);
    }
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <User size={24} weight="duotone" className="step-icon" />
        <h2>פרטים אישיים</h2>
        <p>ספרו לנו קצת על עצמכם</p>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="step-form">
        <div className="form-group">
          <label className="required">שם מלא</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="ד״ר ישראל ישראלי"
            className={touched.fullName && errors.fullName ? "error" : ""}
          />
          {touched.fullName && errors.fullName && (
            <span className="field-error">{errors.fullName}</span>
          )}
        </div>

        <div className="form-group">
          <label className="required">אימייל</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your@email.com"
            dir="ltr"
            className={touched.email && errors.email ? "error" : ""}
          />
          {touched.email && errors.email && (
            <span className="field-error">{errors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label className="required">לשון פנייה</label>
          <p className="field-description">איך תעדיפו שנפנה אליכם במערכת?</p>
          <div className="option-cards">
            {genderOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`option-card ${
                    formData.gender === option.value ? "selected" : ""
                  }`}
                  onClick={() => handleGenderSelect(option.value)}
                >
                  <Icon
                    size={20}
                    weight={
                      formData.gender === option.value ? "fill" : "regular"
                    }
                  />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          {touched.gender && errors.gender && (
            <span className="field-error">{errors.gender}</span>
          )}
        </div>

        <div className="form-group">
          <label>
            מספר רישיון
            <span className="optional-tag">לא חובה</span>
          </label>
          <input
            type="text"
            name="licenseNumber"
            value={formData.licenseNumber}
            onChange={handleChange}
            placeholder="מספר רישיון משרד הבריאות"
            dir="ltr"
          />
          <p className="field-hint">יופיע במסמכים הרשמיים שלכם</p>
        </div>

        <div className="step-actions">
          <Button type="submit" variant="primary" size="large">
            המשך
            <ArrowLeft size={18} weight="bold" />
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AboutYouStep;
